import { Box, Tooltip } from "@mui/material";
import {
  ABDM_ID_FIELDS,
  ABDM_ID_FIELD_NAMES,
  abdmServiceAlert,
  abdmServiceBadge,
} from "utils/abdmServiceStatus";

// Pill colours as in RegisteredDoctorsList's StatusBadge.
const TONES = {
  success: { bg: "#EFFEED", color: "#399F08" },
  error: { bg: "#FEE2E2", color: "#DC2626" },
  warning: { bg: "#FEF3C7", color: "#B45309" },
  default: { bg: "#F3F4F6", color: "#6B7280" },
};

const testIdOf = (field) =>
  field === "hipId" ? "abdm-service-badge" : `abdm-${ABDM_ID_FIELDS[field].key}-badge`;

/**
 * ABDM §3.2.6 result for one of the doctor's ids (the HIP ID by default):
 * Verified, a failure (with the explanation on hover), Not checked, or "-"
 * with no id.
 */
const AbdmServiceBadge = ({ doctor, field = "hipId" }) => {
  const badge = abdmServiceBadge(doctor, field);
  if (!badge) return "-";
  const alert = abdmServiceAlert(doctor, field);
  const tone = TONES[badge.tone] || TONES.default;
  const pill = (
    <Box
      component="span"
      data-testid={testIdOf(field)}
      sx={{
        display: "inline-block",
        bgcolor: tone.bg,
        color: tone.color,
        padding: "0.3rem 0.9rem",
        borderRadius: "25px",
        width: "max-content",
        fontWeight: 600,
        fontSize: "13px",
        whiteSpace: "nowrap",
      }}
    >
      {badge.label}
    </Box>
  );
  return alert ? <Tooltip title={alert.message}>{pill}</Tooltip> : pill;
};

/**
 * The doctors list's ABDM column: a row per id the profile holds, with its
 * name (HIP, HIU, HFR), the id itself and its badge; "-" with none.
 */
export const AbdmIdBadges = ({ doctor }) => {
  const held = ABDM_ID_FIELD_NAMES.filter((field) =>
    String(doctor?.[field] ?? "").trim()
  );
  if (held.length === 0) return "-";
  return (
    <Box display="flex" flexDirection="column" gap={0.5} data-testid="abdm-id-badges">
      {held.map((field) => (
        <Box key={field} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Box
            component="span"
            sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 600, minWidth: 28 }}
          >
            {ABDM_ID_FIELDS[field].key.toUpperCase()}
          </Box>
          <Box
            component="span"
            data-testid={`abdm-${ABDM_ID_FIELDS[field].key}-id`}
            sx={{
              fontFamily: "monospace",
              fontSize: "12px",
              color: "#374151",
              wordBreak: "break-all",
            }}
          >
            {String(doctor[field]).trim()}
          </Box>
          <AbdmServiceBadge doctor={doctor} field={field} />
        </Box>
      ))}
    </Box>
  );
};

export default AbdmServiceBadge;
